import { Status, useInsightPanelDataFetcher } from "./useInsightPanelDataFetcher"
import {
  InsightsPanel,
  InsightsPanelContent,
  InsightsPanelErrorDefault,
  InsightsPanelHeader,
  InsightsPanelNoDataDefault,
} from "~/src/components/Dashboard/InsightsPanel"
import { Format } from "~/src/lib/format"
import { WithErrors } from "~/src/types/withErrors"

type TopUsersDataRow = {
  id: number
  name: string
  href: string
  ordersPlaced: number
  totalSpent: number
}

export type TopUsersData = WithErrors<{ users: TopUsersDataRow[] }>

export type StorefrontInsightsTopUsersPanelRenderProps = {
  status: Status
  users?: TopUsersDataRow[]
}

const NUMBER_OF_USERS_SHOWN = 8

export function StorefrontInsightsTopUsersPanel() {
  const { isLoading, data } = useInsightPanelDataFetcher<TopUsersData>("top_users")

  const users = data?.users

  let status: Status
  if (isLoading) {
    status = "loading"
  } else if (data?.errors) {
    status = "error"
  } else if (users && users.length > 0) {
    status = "success"
  } else {
    status = "noData"
  }

  return <StorefrontInsightsTopUsersRenderPanel status={status} users={users} />
}

export function StorefrontInsightsTopUsersRenderPanel({ status, users }: StorefrontInsightsTopUsersPanelRenderProps) {
  return (
    <InsightsPanel className="@container h-full w-full">
      <InsightsPanelHeader title="Top users" />
      <InsightsPanelContent className="overflow-y-auto">
        <TopUsersContent status={status} rows={users} />
      </InsightsPanelContent>
    </InsightsPanel>
  )
}

function TopUsersContent({ status, rows }: { status: Status; rows: TopUsersDataRow[] | undefined }) {
  return (
    <>
      <div className="h-full @sm:hidden">
        <TopUsersMobileContent status={status} rows={rows} />
      </div>
      <div className="hidden h-full @sm:block">
        <TopUsersDesktopContent status={status} rows={rows} />
      </div>
    </>
  )
}

function TopUsersMobileContent({ status, rows }: { status: Status; rows: TopUsersDataRow[] | undefined }) {
  if (status === "loading") return <TopUsersListSkeleton />
  if (status === "error") return <InsightsPanelErrorDefault />
  if (status === "noData" || !rows) return <InsightsPanelNoDataDefault />

  return <TopUsersList rows={rows} />
}

function TopUsersDesktopContent({ status, rows }: { status: Status; rows: TopUsersDataRow[] | undefined }) {
  if (status === "loading") return <TopUsersTableSkeleton />
  if (status === "error") return <InsightsPanelErrorDefault />
  if (status === "noData" || !rows) return <InsightsPanelNoDataDefault />

  return <TopUsersTable rows={rows} />
}

function TopUsersTable({ rows }: { rows: TopUsersDataRow[] | undefined }) {
  return (
    <table className="max-h-96 w-full overflow-y-auto [&_td]:py-4 [&_th]:py-2">
      <thead className="sticky top-0 bg-slate-200 text-xs font-semibold text-slate-700">
        <tr className="border-b">
          <th className="pl-4 text-left">NAMES</th>
          <th className="text-right">ORDERS PLACED</th>
          <th className="pr-4 text-right">TOTAL SPENT</th>
        </tr>
      </thead>
      <tbody>
        {rows?.map((user) => (
          <tr key={user.name} className="border-b py-4">
            <td className="flex items-center gap-2 pl-4">
              <p>{user.name}</p>
            </td>
            <td className="text-right">{Format.number(user.ordersPlaced)}</td>
            <td className="pr-4 text-right">
              {Intl.NumberFormat("us-EN", { style: "currency", currency: "USD" }).format(user.totalSpent)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function TopUsersList({ rows }: { rows: TopUsersDataRow[] | undefined }) {
  return (
    <>
      {rows?.map((user) => (
        <div key={user.name} className="flex gap-6 border-b p-4">
          <div className="flex flex-col gap-1">
            <p className="mt-[-6px]">{user.name}</p>
            <p className="text-gray-500">Orders placed: {Format.number(user.ordersPlaced)}</p>
            <p className="text-gray-500">
              Total spent: {Intl.NumberFormat("us-EN", { style: "currency", currency: "USD" }).format(user.totalSpent)}
            </p>
          </div>
        </div>
      ))}
    </>
  )
}

function TopUsersTableSkeleton() {
  return (
    <table className="w-full [&_td]:py-4 [&_th]:py-2">
      <thead className="bg-slate-200 text-xs font-semibold text-slate-700">
        <tr className="border-b">
          <th className="pl-4 text-left">NAME</th>
          <th className="text-right">ORDERS PLACED</th>
          <th className="pr-4 text-right">TOTAL SPENT</th>
        </tr>
      </thead>
      <tbody>
        {[...Array(NUMBER_OF_USERS_SHOWN)].map((_, idx) => (
          <tr key={idx} className="animate-pulse border-b">
            <td className="flex items-center gap-2 pl-4">
              <div className="h-3 w-48 rounded-full bg-zinc-200" />
            </td>
            <td className="text-right">
              <div className="ml-auto h-3 w-12 rounded-full bg-zinc-200" />
            </td>
            <td className="pr-4 text-right">
              <div className="ml-auto h-3 w-12 rounded-full bg-zinc-200" />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function TopUsersListSkeleton() {
  return (
    <>
      {[...Array(NUMBER_OF_USERS_SHOWN)].map((_, idx) => (
        <div key={idx} className="flex animate-pulse gap-4 border-b p-4">
          <div className="flex flex-col gap-2">
            <div className="h-2 w-48 rounded-full bg-zinc-200" />
            <div className="h-2 w-24 rounded-full bg-zinc-200" />
            <div className="h-2 w-12 rounded-full bg-zinc-200" />
          </div>
        </div>
      ))}
    </>
  )
}
